// ============================================================
// SHARED TYPES - Single Source of Truth
// Ultimate Trading Terminal
// ============================================================

// ============================================================
// CANDLE & MARKET DATA
// ============================================================

export interface Candle {
  timestamp: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  _isCompleted?: boolean;
}

export interface Tick {
  timestamp: number;
  price: number;
  volume: number;
  bid?: number;
  ask?: number;
}

// Trailing OHLC data for the final N seconds of a candle
// Computed by backend when trailing candle feature is enabled
export interface TrailingOHLC {
  open: number;
  high: number;
  low: number;
  close: number;
  durationSeconds: number;
  isValid: boolean;
}

// Candle with optional trailing OHLC data
export interface CandleWithTrailing extends Candle {
  trailing?: TrailingOHLC;
}

export interface MarketDataUpdate {
  symbol: string;
  candle: Candle;
  isNewCandle: boolean;
  trailing?: TrailingOHLC;
}

export interface MarketData {
  tick: Tick;
  candle?: Candle;
  isNewCandle: boolean;
}

export interface SubscriptionRequest {
  symbol: string;
  action: 'subscribe' | 'unsubscribe';
}

export interface ServerStatus {
  symbols: string[];
  availableSymbols: string[];
  connections: number;
  uptime: number;
}

// ============================================================
// SIGNAL MARKERS
// ============================================================

export type SignalMarkerType =
  | 'bullish'
  | 'bearish'
  | 'entry_buy'
  | 'entry_sell'
  | 'exit'
  | 'volume_spike_bullish'
  | 'volume_spike_bearish'
  | 'trailing_pullback_bullish'
  | 'trailing_pullback_bearish';

export type SkipReason = 'HOD' | 'LOD' | 'SMALL_CANDLE' | 'LOW_ATR';

// Confluence detail for a correlated symbol (moved here for SignalMarker reference)
export interface ConfluenceDetail {
  symbol: string;
  barOffset: number;  // 0 = same bar, -1 = previous bar
  volumeRatio: number | null;
  directionMatch: boolean | null;
  direction: string | null;
  points: number;
  confidence?: number; // Signal confidence score from the correlated symbol
  skipped?: boolean;   // Whether the correlated signal was skipped
}

export interface SignalMarker {
  timestamp: number;
  type: SignalMarkerType;
  /** Symbol this marker belongs to (e.g., "MNQH6", "NQH6") */
  symbol: string;
  candleLength?: number;
  wickLength?: number;
  wickPercentage?: number;
  volumeRatio?: number;
  isProfitable?: boolean;
  pnl?: number;
  // MAE/MFE tracking (Maximum Adverse/Favorable Excursion)
  /** Maximum Adverse Excursion in points (max drawdown before resolution) */
  mae?: number;
  /** MAE as percentage of stop loss (0-100+) */
  maePercent?: number;
  /** Maximum Favorable Excursion in points (max profit before resolution) */
  mfe?: number;
  /** MFE as percentage of profit target (0-100+) */
  mfePercent?: number;
  skipped?: boolean;
  skipReason?: SkipReason;
  atrValue?: number;
  atrLabel?: string;
  confidence?: number;
  // Confluence (for pinning volume and WCC markers)
  confluenceScore?: number;
  confluenceSymbols?: string[];  // Base symbols that contributed to confluence
  confluenceDetails?: ConfluenceDetail[];
  confluenceGroup?: string;
  // ML Scoring (0-100 score, TAKE/SKIP recommendation)
  mlScore?: number;
  mlRecommendation?: 'TAKE' | 'SKIP';
}

// Pinning Volume Analysis - detailed per-candle analysis for info overlay
export interface PinningVolumeAnalysis {
  timestamp: number;

  // Candle OHLC (for debugging - which candle is actually being analyzed)
  candleOpen: number;
  candleHigh: number;
  candleLow: number;
  candleClose: number;

  // Prior candle info (for debugging prior move calculation)
  priorCandleOpen: number;  // Open of the candle N periods ago (where N = priorMoveLookback)
  priorMoveLookback: number;  // How many candles back we looked

  // Volume Metrics
  currentVolume: number;
  averageVolume: number;
  volumeRatio: number;
  volumeStdDev: number;
  volumeMultiplier: number;
  volumeSpikeThreshold: number;
  isVolumeSpike: boolean;
  prevCandleVolumeSpike: boolean;      // index-1
  prev2CandleVolumeSpike: boolean;     // index-2
  volumeSpikeSource: 'current' | 'prev-1' | 'prev-2' | null;  // which candle provided valid spike

  // Wick Metrics
  upperWickPct: number;
  lowerWickPct: number;
  dominantWickPct: number;
  hasSignificantWick: boolean;
  wickDirection: 'upper' | 'lower' | 'none';

  // Prior Move Metrics
  priorMovePoints: number;
  priorMoveDirection: 'up' | 'down' | 'neutral';

  // Alignment Check
  isWickAligned: boolean;
  expectedWickDirection: 'upper' | 'lower' | 'none';

  // ATR Context
  atrValue: number;
  atrLabel: string | null;
  atrMinThreshold: number;
  passesAtrFilter: boolean;

  // Filter Status
  isNearHOD: boolean;
  isNearLOD: boolean;
  distanceFromHOD: number;
  distanceFromLOD: number;
  passesHodFilter: boolean;
  passesLodFilter: boolean;
  passesMinCandleLength: boolean;
  passesMinVolumeRatio: boolean;
  candleLength: number;
  minCandleLengthPoints: number;
  minVolumeRatioSetting: number;

  // Final Verdict
  isSignal: boolean;
  signalType: 'bullish' | 'bearish' | null;
  rejectionReasons: string[];

  // Confluence (optional, added when confluence is enabled)
  confluenceScore?: number;
  confluenceDetails?: ConfluenceDetail[];
  confluenceGroup?: string;
}

// Whole Candle Analysis - detailed per-candle analysis for info overlay
export interface WholeCandleAnalysis {
  timestamp: number;

  // Candle OHLC (for reference)
  open: number;
  high: number;
  low: number;
  close: number;
  isGreen: boolean;

  // Core Wick Metrics
  upperWickPct: number;
  lowerWickPct: number;
  combinedWickPct: number;
  candleLength: number;

  // Wick Threshold Evaluation
  wickThresholdMin: number;
  wickThresholdMax: number;
  isWickInRange: boolean;

  // ATR Metrics
  atrValue: number;
  atrMinThreshold: number;
  skipIfLowATR: boolean;
  passesAtrFilter: boolean;

  // HOD/LOD Metrics
  sessionHigh: number;
  sessionLow: number;
  distanceFromHOD: number;
  distanceFromLOD: number;
  hodLodRangePoints: number;
  skipIfHOD: boolean;
  skipIfLOD: boolean;
  isNearHOD: boolean;
  isNearLOD: boolean;
  passesHodFilter: boolean;
  passesLodFilter: boolean;

  // Small Candle Filter
  minCandleLengthPoints: number;
  skipIfSmallCandle: boolean;
  passesLengthFilter: boolean;

  // Final Verdict
  isSignal: boolean;
  signalType: 'bullish' | 'bearish' | null;
  wasSkipped: boolean;
  skipReason: SkipReason | null;
  rejectionReasons: string[];

  // Confidence Scoring (optional, added when confidence is enabled)
  confidence?: number;
  confidenceFactors?: Array<{
    name: string;
    price: number;
    positionPct: number;
    zone: 'low' | 'mid' | 'high';
    contribution: number;
    weight: number;
  }>;
  emaStackAligned?: boolean;
  vwapAligned?: boolean;
  levelDensity?: number;
  nextLevelDistance?: number;
}

// ============================================================
// DAILY SWEEP STRATEGY (SFP / Swing Points)
// ============================================================





// ============================================================
// ICT STRATEGY TYPES
// ============================================================

/** ICT Timeframe for FVG detection */
export type ICTTimeframe = '2m' | '5m' | '15m' | '1h' | '4h';

/** Type of Fair Value Gap zone */
export type ZoneType = 'bullish' | 'bearish';

/** Fair Value Gap zone */
export interface FVGZone {
  /** Unique identifier for the zone */
  id: string;
  /** Symbol this zone belongs to (e.g., "MNQH6", "NQH6") */
  symbol: string;
  /** Timeframe where this FVG was detected */
  timeframe: ICTTimeframe;
  /** Timestamp when the gap formed (middle candle of 3-pattern) */
  startTime: number;
  /** Timestamp when the gap was completely filled (null if still open) */
  endTime: number | null;
  /** Upper edge of the gap (higher price) */
  highPrice: number;
  /** Lower edge of the gap (lower price) */
  lowPrice: number;
  /** Type of gap (bullish or bearish) */
  zoneType: ZoneType;
  /** Whether this gap has been converted to IFVG (fully filled and now inverse) */
  isInverse: boolean;
  /** Whether the gap has been completely filled */
  isFilled: boolean;
  /** Percentage of the gap that has been filled (0-100) */
  fillPercentage: number;
  /** ATR value when the gap was created (for size filtering) */
  atrAtCreation: number;
  /** Timestamp of the first candle in the 3-pattern */
  candle1Timestamp: number;
  /** Timestamp of the third candle in the 3-pattern */
  candle3Timestamp: number;
}

/** ICT Strategy settings */
export interface ICTSettings {
  /** Enable/disable the entire ICT strategy */
  enabled: boolean;

  // === FVG SETTINGS ===
  /** Enable FVG detection */
  fvgEnabled: boolean;
  /** Timeframes to detect FVGs on */
  fvgTimeframes: ICTTimeframe[];
  /** Minimum gap size as ATR multiple (e.g., 0.25 = 25% of ATR) */
  fvgMinGapAtrMultiple: number;
  /** Lookback periods per timeframe */
  fvgLookbackByTimeframe: Record<string, number>;
  /** Show filled/mitigated zones (dimmed) */
  fvgShowFilled: boolean;
  /** Opacity for filled zones (0-1) */
  fvgFilledOpacity: number;
  /** Opacity for unfilled/active zones (0-1) */
  fvgUnfilledOpacity: number;
  /** Color for bullish FVG zones (hex) */
  fvgBullishColor: string;
  /** Color for bearish FVG zones (hex) */
  fvgBearishColor: string;

  // === IFVG SETTINGS ===
  /** Enable IFVG detection (inverse FVG after complete fill) */
  ifvgEnabled: boolean;
  /** Timeframes to detect IFVGs on */
  ifvgTimeframes: ICTTimeframe[];
  /** Minimum gap size as ATR multiple for IFVG */
  ifvgMinGapAtrMultiple: number;
  /** Lookback periods per timeframe for IFVG */
  ifvgLookbackByTimeframe: Record<string, number>;
  /** Show filled IFVG zones */
  ifvgShowFilled: boolean;
  /** Opacity for filled IFVG zones */
  ifvgFilledOpacity: number;
  /** Opacity for active IFVG zones */
  ifvgUnfilledOpacity: number;
  /** Color for bullish IFVG zones (hex) */
  ifvgBullishColor: string;
  /** Color for bearish IFVG zones (hex) */
  ifvgBearishColor: string;

  // === DISPLAY TOGGLES ===
  /** Render FVG zones on chart (detection still runs) */
  showFvgOnChart: boolean;
  /** Render IFVG zones on chart (detection still runs) */
  showIfvgOnChart: boolean;
  /** Show timeframe+age badges on FVG/IFVG zones */
  showZoneBadges: boolean;

  /** Timestamp when settings were last updated */
  updatedAt?: number;
}

/** Default ICT settings */
export const DEFAULT_ICT_SETTINGS: ICTSettings = {
  enabled: false,
  fvgEnabled: true,
  fvgTimeframes: ['2m', '5m', '15m'],
  fvgMinGapAtrMultiple: 0.25,
  fvgLookbackByTimeframe: { '2m': 100, '5m': 50, '15m': 30, '1h': 72, '4h': 36 },
  fvgShowFilled: true,
  fvgFilledOpacity: 0.2,
  fvgUnfilledOpacity: 0.4,
  fvgBullishColor: '#22c55e',
  fvgBearishColor: '#ef4444',
  ifvgEnabled: false,
  ifvgTimeframes: ['15m', '1h'],
  ifvgMinGapAtrMultiple: 0.25,
  ifvgLookbackByTimeframe: { '15m': 30, '1h': 72, '4h': 36 },
  ifvgShowFilled: true,
  ifvgFilledOpacity: 0.2,
  ifvgUnfilledOpacity: 0.4,
  ifvgBullishColor: '#3b82f6',
  ifvgBearishColor: '#f97316',
  showFvgOnChart: true,
  showIfvgOnChart: true,
  showZoneBadges: true,
};

// ============================================================
// ATR & DYNAMIC THRESHOLDS
// ============================================================

export interface ATRThresholdMapping {
  atrMin: number;
  atrMax: number;
  volumeMultiplier: number;
  minVolumeRatio: number;
  label: string;
}

export interface DynamicThresholdConfig {
  enabled: boolean;
  mappings: ATRThresholdMapping[];
  fallbackVolumeMultiplier: number;
  fallbackMinVolumeRatio: number;
  lastOptimizedAt?: number | null;
  optimizationMetric?: 'winRate' | 'profitFactor' | 'expectancy';
}

// ============================================================
// STRATEGY SETTINGS
// ============================================================

// Whole Candle Continuation Strategy Settings
export interface WholeCandleSettings {
  enabled: boolean;
  wickThresholdMin: number;
  wickThresholdMax: number;
  skipIfHOD: boolean;
  skipIfLOD: boolean;
  hodLodRangePoints: number;
  skipIfLowATR: boolean;
  atrMinThreshold: number;
  skipIfSmallCandle: boolean;
  minCandleLengthPoints: number;
  /** Min candle length for ES/MES (smaller price range than NQ/MNQ) */
  minCandleLengthPointsEs?: number;
  tpPoints: number;
  slPoints: number;
  sessionStartHour: number;
  sessionEndHour: number;
  tradeExecutionEnabled: boolean;
  predictorEnabled: boolean;
  predictorAlertThreshold: number;
  predictorStartTrackingAt: number;
  // Preemptive signal timing (seconds before bar close, default 1)
  preemptiveSeconds: number;
  updatedAt: number;

  // Confidence Scoring Settings
  confidenceEnabled: boolean;
  confidenceMinThreshold: number;
  // Level Weights (0 = disabled, 1.0 = normal weight)
  weightEma9: number;
  weightEma21: number;
  weightEma200: number;
  weightVwap: number;
  weightFib382: number;
  weightFib50: number;
  weightFib618: number;
  weightFib705: number;  // Stop-hunt pocket
  weightFib786: number;
  weightHod: number;
  weightLod: number;
  // Group toggles (convenience for UI - when false, sets respective weights to 0)
  useEmas: boolean;
  useFibs: boolean;
  useVwap: boolean;
  useHodLod: boolean;
  confidenceLowZonePct: number;
  confidenceHighZonePct: number;
  confidenceBaseScore: number;
  confidenceBoostPerLevel: number;
  confidencePenaltyPerLevel: number;
  emaStackBonus: number;
  vwapAlignmentBonus: number;
  levelDensityThreshold: number;
  levelDensityPenalty: number;
  nextLevelCloseThreshold: number;
  nextLevelClosePenalty: number;

  // Confluence Scoring Settings
  /** Enable confluence scoring - check correlated symbols for signals on same bar */
  confluenceEnabled?: boolean;
  /** Include skipped signals when calculating confluence */
  confluenceIncludeSkipped?: boolean;
  /** Show markers from other correlated symbols on this symbol's chart */
  showOtherSymbolSignals?: boolean;

  // "TAKE IT" Indicator Settings
  /** Threshold for "TAKE IT" indicator (confidence + confluence must exceed this) */
  takeItThreshold?: number;
  /** Enable "TAKE IT" indicator display */
  takeItEnabled?: boolean;

  // Skipped Signal Penalty Settings
  /** Confidence penalty applied to skipped signals (default: 40 points) */
  skippedSignalPenalty?: number;
  /** If true, set confidence to 0 for skipped signals (overrides penalty) */
  skippedZeroConfidence?: boolean;
}

// Confluence group configuration
export interface ConfluenceGroup {
  name: string;
  symbols: string[];
  fullContracts: string[];
  microContracts: string[];
}

// Pinning Volume Strategy Settings
export interface PinningVolumeSettings {
  enabled: boolean;
  volumeMultiplier: number;
  volumeLookback: number;
  minVolumeRatio: number;
  minWickPercentage: number;
  priorMoveLookback: number;
  useWickDirection: boolean;
  requireAlignedWick: boolean;
  skipIfLowATR: boolean;
  atrMinThreshold: number;
  skipIfHOD: boolean;
  skipIfLOD: boolean;
  hodLodRangePoints: number;
  skipIfSmallCandle: boolean;
  minCandleLengthPoints: number;
  /** Min candle length for ES/MES (smaller price range than NQ/MNQ) */
  minCandleLengthPointsEs?: number;
  tradeExecutionEnabled: boolean;
  tpPoints: number;
  slPoints: number;
  dynamicThresholds?: DynamicThresholdConfig;
  // Confluence settings
  confluenceEnabled?: boolean;
  confluenceGroups?: ConfluenceGroup[];
}

export interface StrategyConfig {
  name: string;
  symbol: string;
  timeframe: string;
  enabled: boolean;
  params?: Record<string, unknown>;
}

// ============================================================
// ORDERS & SIGNALS
// ============================================================

export type OrderAction = 'BUY' | 'SELL';
export type OrderType = 'MARKET' | 'LIMIT' | 'STOP' | 'STOP_LIMIT';
export type OrderStatus = 'PENDING' | 'FILLED' | 'CANCELLED' | 'REJECTED';

export interface Signal {
  id?: string;
  action: OrderAction;
  type: OrderType;
  quantity: number;
  price?: number;
  stopLoss?: number;
  takeProfit?: number;
  reason: string;
  timestamp?: number;
}

export interface Order extends Signal {
  id: string;
  status: OrderStatus;
  filledPrice?: number;
  filledQuantity?: number;
  strategyName: string;
}

// ============================================================
// POSITIONS & ACCOUNT
// ============================================================

export interface Position {
  symbol: string;
  quantity: number;
  avgPrice: number;
  currentPrice: number;
  unrealizedPnL: number;
  realizedPnL: number;
}

export interface RiskParams {
  maxPositionSize: number;
  maxDailyLoss: number;
  riskPerTrade: number;
}

export interface Account {
  balance: number;
  equity: number;
  marginUsed: number;
  marginAvailable: number;
}

// ============================================================
// SESSION & CONFIG
// ============================================================

export interface SessionHighLow {
  high: number;
  highTimestamp: number;
  low: number;
  lowTimestamp: number;
  sessionStart: number;
}

export type DataMode = 'live' | 'backtest';
export type StrategyMode = 'paper' | 'live';
export type MarketStatus = 'open' | 'closed' | 'pre-market' | 'post-market';

// ============================================================
// STRATEGY EVENTS
// ============================================================

export interface StrategyUpdate {
  strategyName: string;
  enabled: boolean;
  inPosition: boolean;
  positionType: 'LONG' | 'SHORT' | null;
  entryPrice: number;
}

export interface StrategySignalEvent {
  strategyName: string;
  signal: Signal;
  timestamp: number;
}

export interface StrategyCommentary {
  strategyName: string;
  message: string;
  type: 'info' | 'signal' | 'entry' | 'exit' | 'warning' | 'error';
  timestamp: number;
}

// ============================================================
// BACKTEST TYPES
// ============================================================

export type DateRangePreset = 'today' | 'yesterday' | 'last-week' | 'last-month' | 'last-3-months' | 'custom';

export interface BacktestRequest {
  symbol: string;
  startDate: number;
  endDate: number;
  barInterval?: number;
}

export interface BacktestConfig {
  startDate: Date;
  endDate: Date;
  preset: DateRangePreset;
  initialSeekTimestamp?: number;
}

export interface BacktestDataResponse {
  symbol: string;
  candles: Candle[];
  startDate: number;
  endDate: number;
  totalCandles: number;
  mode: 'backtest';
}

export interface BacktestSessionMetadata {
  id: string;
  name: string;
  symbol: string;
  startDate: number;
  endDate: number;
  createdAt: number;
  completedAt?: number;
  status: 'in_progress' | 'completed' | 'abandoned';
}

// ============================================================
// PAPER TRADING TYPES
// ============================================================

export interface PaperTradingConfig {
  initialBalance: number;
  commission: number;
  slippageTicks: number;
  tickSize: number;
  tickValue: number;
  maxPositionSize: number;
  enableSlippage: boolean;
  enableCommission: boolean;
}

export interface PaperPosition extends Position {
  entryTime: number;
  orderId: string;
  strategyName?: string;
}

export interface PaperOrder extends Order {
  commissionPaid: number;
  slippage: number;
  executionTime?: number;
}

export interface PendingOrder {
  id: string;
  action: OrderAction;
  type: 'BUY_LIMIT' | 'SELL_LIMIT' | 'BUY_STOP' | 'SELL_STOP';
  quantity: number;
  price: number;
  strategyName: string;
  reason: string;
  timestamp: number;
  symbol: string;
  ocoGroupId?: string;
}

export interface TradeLogEntry {
  id: string;
  timestamp: number;
  symbol: string;
  action: OrderAction;
  quantity: number;
  price: number;
  commission: number;
  slippage: number;
  pnl?: number;
  balanceAfter: number;
  positionAfter: number;
  mode?: 'live' | 'backtest';
  backtestSessionId?: string;
  notes?: string;
}

export interface PaperTradingPersistenceData {
  balance: number;
  equity: number;
  totalPnL: number;
  realizedPnL: number;
  positions: PaperPosition[];
  orders: PaperOrder[];
  pendingOrders: PendingOrder[];
  tradeLog: TradeLogEntry[];
  stats: {
    totalTrades: number;
    winningTrades: number;
    losingTrades: number;
    winRate: number;
    profitFactor: number;
    largestWin: number;
    largestLoss: number;
  };
  lastUpdated: number;
}

export interface BacktestSessionData extends PaperTradingPersistenceData {
  sessionId: string;
  metadata: BacktestSessionMetadata;
}

// ============================================================
// SIGNAL PREDICTOR TYPES
// ============================================================

export interface LiveCandleOHLC {
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  isGreen: boolean;
}

export interface SignalPrediction {
  probability: number;
  trend: 'rising' | 'falling' | 'stable';
  direction: 'LONG' | 'SHORT' | null;
  wickPct: number;
  candleLength: number;
  isQualifying: boolean;
  atr: number;
  atrMinThreshold: number;
  isLowATR: boolean;
  marketStatus: MarketStatus;
  marketMessage?: string;
  liveCandle?: LiveCandleOHLC;
  elapsedSeconds: number;
  secondsToEvaluation: number;
  candleStartTime: number;
  willBeSkipped: boolean;
  skipReason: SkipReason | null;
  skipDistance: number;
  alertTriggered: boolean;
  alertLevel: 'none' | 'watch' | 'ready' | 'imminent';
  lastCandleResult?: {
    wasSignal: boolean;
    direction: 'LONG' | 'SHORT' | null;
    wasSkipped: boolean;
    skipReason: SkipReason | null;
    wickPct?: number;
    thresholdMax?: number;
    alertWasSent?: boolean;
    atr?: number;
  };
}

export interface PredictorSettings {
  enabled: boolean;
  alertThreshold: number;
  startTrackingAt: number;
}

// ============================================================
// AI PREDICTION TYPES
// ============================================================

export interface PredictedCandle {
  timestamp: number;
  open: number;
  high: number;
  low: number;
  close: number;
  confidence: number;
}

export interface TrendProbabilities {
  up: number;
  down: number;
  sideways: number;
}

export interface AttentionWeights {
  timestamps: number[];
  weights: number[];
}

export interface PredictionResponse {
  symbol: string;
  predictions: PredictedCandle[];
  trend_probabilities: TrendProbabilities;
  attention_weights: AttentionWeights;
  model_version: string;
  inference_time_ms: number;
  metadata?: {
    num_input_candles: number;
    timeframe_minutes: number;
    has_multi_timeframe: boolean;
  };
}

export interface PredictionRequest {
  symbol: string;
  candles: Candle[];
  timeframe_minutes: number;
  multi_timeframe_data?: {
    [key: string]: Candle[];
  };
  indicators?: {
    [key: string]: number[];
  };
}

// ============================================================
// TRADOVATE TYPES
// ============================================================

export interface TradovateAuth {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
  userId: number;
  accountId: number;
}

export interface TradovateCredentials {
  username: string;
  password: string;
  appId: string;
  appVersion: string;
  cid: string;
  sec: string;
}

// ============================================================
// SUPPORT/RESISTANCE TYPES
// ============================================================

/** S/R timeframe for multi-timeframe analysis */
export type SRTimeframe = '5m' | '15m' | '1h' | '4h' | 'D' | 'W';

/** Level type: support or resistance */
export type SRLevelType = 'support' | 'resistance';

/** Support/Resistance level with full metadata */
export interface SRLevel {
  /** Price level */
  price: number;
  /** Support or resistance */
  levelType: SRLevelType;
  /** Timeframe this level was derived from */
  timeframe: SRTimeframe;
  /** Number of touches at this level */
  touchCount: number;
  /** Calculated strength score (1-10) */
  strengthScore: number;
  /** Cross-timeframe confluence score (1-5) */
  confluenceScore: number;
  /** Other timeframes that have levels at this price */
  confluentTimeframes: SRTimeframe[];
  /** When level first formed (timestamp) */
  firstTouch: number;
  /** Most recent touch (timestamp) */
  lastTouch: number;
  /** Last recalculation time */
  updatedAt: number;
  /** Recent touch timestamps for tooltip (last 10) */
  touchTimestamps?: number[];
}

/** S/R levels update payload from backend */
export interface SRLevelsUpdate {
  symbol: string;
  levels: SRLevel[];
  timestamp: number;
}

/** S/R settings */
export interface SRSettings {
  enabled: boolean;
  deltaPercent: number;
  recalcIntervalSecs: number;
}

// ============================================================
// TRAILING PULLBACK STRATEGY
// ============================================================

/**
 * Trailing Pullback Strategy settings
 * Generates signals when trailing candles show healthy pullback patterns
 */
export interface TrailingPullbackSettings {
  /** Whether the strategy is enabled */
  enabled: boolean;
  /** Minimum confidence threshold to emit signal (0-100) */
  confidenceThreshold: number;
  /** Include continuation patterns (same direction trailing) */
  includeContinuation: boolean;
  /** Require matching WCC signal for alignment */
  requireWccAlignment: boolean;
  /** Minimum trailing body size as percentage of main candle */
  minTrailingBodyPct: number;
  /** Last update timestamp */
  updatedAt: number;
}


